import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Box, Center, Flex, StyleProps, Text, XOutline } from '@bonusly/design-system';

import { User } from '../../../schemas/users';
import { InlineSelector } from './inline-selector';

type UserSelectorProps<T extends User | string> = StyleProps & {
	autoCommitOnBlur?: boolean;
	onChange: (users: T[]) => void;
	onCreate?: (value: string) => void;
	onSearch: (query: string) => Promise<User[]>;
	placeholder?: string;
	renderCreateLabel?: (value: string) => ReactElement;
	limit?: number;
	value: T[];
};

const toEmail = (user: User | string) => (typeof user === 'string' ? user : user.email);

export const UserEmailSelector = <T extends User | string>({ onChange, onSearch, value, ...rest }: UserSelectorProps<T>) => {
	const { t } = useTranslation('components', { keyPrefix: 'user_selector' });

	return (
		<InlineSelector
			aria-label={t('placeholder')}
			emptyState={
				<Center py={4}>
					<Text color="gray.500">{t('empty_state')}</Text>
				</Center>
			}
			onRemove={(user) => onChange(value.filter((u) => toEmail(u) !== toEmail(user)))}
			onSearch={onSearch}
			onSelect={(user) => onChange([...value, user])}
			placeholder={t('placeholder')}
			value={value}
			renderSelectedItem={({ isSelected, onRemove, value: user }) => (
				<Flex
					alignItems="center"
					aria-label={t('selected_label', { name: toEmail(user) })}
					as="span"
					bgColor="primary.bgAlpha"
					borderRadius="full"
					color="primary.fgStrong"
					display="inline-flex"
					gap={1}
					px={3}
					py={1}
					_selected={{
						outline: '2px solid',
						outlineColor: 'border.focus',
					}}
					{...(isSelected && {
						'data-selected': true,
					})}
				>
					{toEmail(user)}

					<Center as="button" onMouseDown={onRemove} type="button">
						<XOutline size="xs" />
					</Center>
				</Flex>
			)}
			// Menu items will always be user objects, not strings
			renderMenuItem={({ isSelected, option: user, onSelect }) => (
				<Flex
					as="button"
					aria-label={t('selected_label', { name: user.username })}
					borderRadius="sm"
					onClick={onSelect}
					px={3}
					py={1}
					transition="background-color 0.2s ease-in-out"
					type="button"
					w="full"
					_hover={{
						bgColor: 'primary.bgAlpha',
					}}
					_selected={{
						bgColor: 'primary.bgAlpha',
					}}
					{...(isSelected && {
						'data-selected': true,
					})}
				>
					<Avatar name={user.displayName} mr={3} size="lg" src={user.profilePic} />

					<Box flex={1} minWidth={0} textAlign="left">
						<Text fontSize="lg" fontWeight={500} lineHeight={7} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
							{user.displayName}
						</Text>

						<Text color="gray.600" fontSize="sm" fontWeight="normal" lineHeight={5} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
							{user.email}
						</Text>
					</Box>
				</Flex>
			)}
			{...rest}
		/>
	);
};
